/**
 * Shared itinerary types for the travel planner.
 * Used by API, mobile app, and import/export.
 */

/**
 * Like `Partial<T>` but each field also accepts explicit `undefined`.
 *
 * Patch APIs use this so callers can clear an existing field by
 * passing `{ field: undefined }` — the same wire shape they'd send
 * for "leave as-is" stays a no-op, and an actual clear stays an
 * explicit `undefined`. Required under `exactOptionalPropertyTypes`
 * which would otherwise reject the explicit `undefined`.
 *
 * ADR-0084 chat-Undo runtime escape hatch: at the adapter layer,
 * `applyPatch` (apps/api/src/lib/itinerary-service-adapter.ts) also
 * accepts `null` per key as "delete this field." It's not in this
 * type union because the user-driven sync path threads Patch<T>
 * through functions that don't accept null. Callers that want the
 * delete semantic (only the assistant audit-revert path today)
 * cast through `Record<string, unknown>`.
 */
export type Patch<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Like `Patch<T>` but each field also accepts `null` as an explicit
 * "clear this field" signal (delete the key, restoring its absent
 * default). `undefined` / omitted stays a no-op.
 *
 * The client-side `applyPatch` (apps/mobile/src/store/helpers.ts)
 * honors this, matching the `null`-deletes convention the API-side
 * `applyPatch` already implements. Used by slice update actions whose
 * inputs are user-edited form fields that can be emptied back out
 * (e.g. a journey's carrier, a stay's room type).
 */
export type ClearablePatch<T> = { [K in keyof T]?: T[K] | null };

// ─── Shared types (ADR-0043) ─────────────────────────────────────

/**
 * Authorship attribution for an itinerary entity — ADR-0084.
 *
 * Tags whether a Place / CustomEntry / Transport / Journey / Stay was
 * added by the user directly or written by the embedded trip assistant
 * (chat). When unset, treat as `'user'` — the existing population of
 * entities predates this field, and the chat assistant was read-only
 * until ADR-0084. Slice 2 stamps these on every Tier-A write the
 * dispatcher commits; Slice 3 extends to Tier-B/C commits.
 *
 * `assistantInitiatedBy` is denormalised onto the entity (rather than
 * looked up through `AssistantConversations`) because conversations
 * live in per-user partitions; a collaborator viewing a shared trip
 * cannot read the originating user's conversation table. Carrying the
 * user id + turn id on the entity lets the shared-trip card render
 * "Added by AI (via Sarah)" without a cross-partition read.
 */
export type EntityAuthor = 'user' | 'assistant';

export interface AssistantAttribution {
  /** User-id of the caller whose chat produced this write. For shared
   *  trips this is the SHARER who chatted, not the trip owner. The UI
   *  resolves the display name from the existing trip-share roster. */
  userId: string;
  /** `AssistantMessage.id` of the user-side message that produced the
   *  write (the model dispatched the tool while answering this turn).
   *  Used to back-reference the originating conversation entry without
   *  loading the full chat. */
  turnId: string;
  /** ISO datetime of the commit. */
  at: string;
}

/** Mixin applied to every Tier-A/B/C-writable entity in slice 2. The
 *  three fields are independent: a newly-AI-added place that the user
 *  later edits becomes `createdBy: 'assistant', lastModifiedBy: 'user'`,
 *  and the AI badge fades on the next render. */
export interface EntityAuthorshipFields {
  /** Who originally added the entity. Defaults to `'user'` on read
   *  when absent so pre-attribution rows render correctly. */
  createdBy?: EntityAuthor | undefined;
  /** Who most recently modified the entity. Cleared back to `'user'`
   *  on the next user-driven edit so the "Added by AI" badge ages out. */
  lastModifiedBy?: EntityAuthor | undefined;
  /** Set when `createdBy === 'assistant'` OR `lastModifiedBy ===
   *  'assistant'`. Carries the originating user + turn + timestamp so
   *  shared-trip viewers can attribute the change without reading the
   *  per-user conversation partition. */
  assistantInitiatedBy?: AssistantAttribution | undefined;
}

/**
 * Photo author attribution as required by Google Maps Platform ToS
 * §3.2.3 — every Place Photos surface must display the photographer's
 * name (and link, when provided). Captured at catalog lookup time so
 * the UI can render attribution alongside the photo without an extra
 * fetch. `displayName` is the only required field; `uri` is the
 * photographer's profile link (may be a Google Maps user URL).
 */
export interface PhotoAttribution {
  displayName: string;
  uri?: string | undefined;
}

/**
 * Slim, durable snapshot of a place picked from the catalog. Embedded
 * on `UserPreferences.home` and `Trip.destinationPlace` so the two
 * consumers share one shape. See ADR-0054. Distinct from `Place` (per-
 * day item with startTime/endTime/cost) and from `PlaceCatalogEntry`
 * (catalog-search shape carrying photoToken/rating/openingHours we
 * don't persist).
 */
export interface PlaceRef {
  /** Catalog id ('<source>:<native>') from the picker. */
  id: string;
  /** Display name from the catalog at pick time. Denormalized so
   *  display doesn't require a fresh lookup. */
  name: string;
  /** Free-text formatted address from the catalog at pick time. */
  address?: string | undefined;
  /** 2-decimal-rounded latitude (~1 km precision). Absent for catalog
   *  rows without coords (rare). */
  lat?: number | undefined;
  /** 2-decimal-rounded longitude. */
  lng?: number | undefined;
  /** ISO 3166-1 alpha-2 derived server-side from the address tail. */
  countryCode?: string | undefined;
  /** Opaque photo proxy token (e.g. 'google:<placeId>|<photoRef>')
   *  captured at pick time so anchors / stays / home / destination can
   *  show a hero image without persisting the (signed, short-lived)
   *  Google CDN URL. Cleared when the user clears the place. */
  photoToken?: string | undefined;
  /** First-attribution snapshot for the chosen photo. Persisted so the
   *  ToS-required photographer credit can be rendered without a fresh
   *  Google round trip. Google returns an array; we keep the first
   *  entry (matches the photo we keep — `photos[0]`). */
  photoAttribution?: PhotoAttribution | undefined;
}

/** 'HH:mm' — a scheduled slot within a single day. */
export interface TimeRange {
  startTime?: string | undefined;
  endTime?: string | undefined;
}

/** 'YYYY-MM-DD' — calendar date span. */
export interface DateRange {
  startDate: string;
  endDate: string;
}

/** Markdown text. Alias so we can upgrade to { content; updatedAt } later. */
export type Note = string;

/** A money amount paired with an ISO 4217 currency code (e.g. 'USD',
 *  'EUR', 'JPY'). Currency is intentionally a free-form string rather
 *  than an enum so users can enter local codes we haven't pre-loaded
 *  ('UYU', 'BTN'...) without us shipping an update. The amount is a
 *  major-unit number (12.50, not 1250 cents) — JS-friendly and what
 *  the input UI types directly. */
export interface Money {
  amount: number;
  currency: string;
}

/** Reservation metadata shared by anything bookable
 *  (Journey, Stay, future Group/Tour, optionally Place). */
export interface Booking {
  ref?: string | undefined;
  confirmationCode?: string | undefined;
  /** 'United', 'Expedia', tour-operator name, etc. */
  provider?: string | undefined;
  bookingUrl?: string | undefined;
  /** Per-item costs for this booking — replaces the single
   *  `pricePaid` of ADR-0048. See `CostLineItem` and ADR-0069.
   *  Legacy `pricePaid` records are folded into a single-item
   *  `costs` array on hydrate. */
  costs?: CostLineItem[] | undefined;
  /** @deprecated ADR-0060 — attachments now live on `Trip.attachments`
   *  with `attachedTo: { kind: 'booking', entityId: <journeyId|stayId> }`.
   *  Field retained for one release so persisted local data can hydrate;
   *  sync-manager strips it on flush. Always undefined / empty on new
   *  writes. */
  attachments?: string[] | undefined;
}

/**
 * A single line of out-of-pocket spend on a cost-bearing entity —
 * ADR-0069. Replaces the single-`Money` storage of ADR-0048: a
 * museum visit can carry ticket + audio-guide + lunch as three
 * independently attributable items.
 *
 * Attribution lives inline (no sidecar): each item carries its
 * own optional `paidBy` / `splitWith` / `categoryId`. Deleting
 * the parent entity drops the items automatically — no orphan
 * cleanup pass. Stale `paidBy` / `splitWith` / `categoryId`
 * refs are tolerated at read time (skipped) and pruned on
 * hydrate by `pruneTripCosts`.
 */
export interface CostLineItem {
  /** Client-minted via `generateId()`. Stable across edits so
   *  optimistic updates and analytics can target a specific
   *  item. */
  id: string;
  /** Free-form display label — "Ticket", "Audio guide", "Tip".
   *  Absent for single-item entities (the default state after
   *  migration); the UI falls back to the entity name. */
  label?: string | undefined;
  /** Major-unit amount, mirrors the legacy `Money.amount`
   *  semantics. Read-time validation drops items with non-finite
   *  or non-positive amounts. */
  amount: number;
  /** ISO 4217 currency code. Per-item — entities can mix
   *  currencies (a USD ticket + a local-cash tip). Group-by-
   *  currency rollups already exist one level up. */
  currency: string;
  /** TripParticipant.id. Optional — absent means "unattributed"
   *  (skipped by settlement, surfaced in the "needs attribution"
   *  filter on the Review tab). */
  paidBy?: string | undefined;
  /** TripParticipant.ids — people the cost is split equally
   *  across. Absent expands to "all current participants" at
   *  read time. Empty array pins the cost to `paidBy` alone. */
  splitWith?: string[] | undefined;
  /** TripCategory.id. Stale ids tolerated at read time, pruned
   *  on hydrate. */
  categoryId?: string | undefined;
}

export interface TripPreferences {
  active?: boolean | undefined;
  relaxed?: boolean | undefined;
  kids?: boolean | undefined;
  interests?: string[] | undefined;
}

export interface Place extends TimeRange, EntityAuthorshipFields {
  id: string;
  /** @deprecated ADR-0044 — prefer `catalogId` (carries the source
   *  prefix). Removed once all consumers migrate. */
  placeId: string;
  /** Canonical name from Google Places. Treat as the underlying truth. */
  name: string;
  /** Optional user-provided display name override. When set, the UI shows
   *  this instead of `name` (e.g. rename "Starbucks Reserve" to "Sara's
   *  coffee"). The Google data, photo, address, lat/lng and travel-time
   *  pairing all stay tied to the original `name` / `placeId`. */
  customLabel?: string | undefined;
  /** @deprecated ADR-0044 — prefer `photoToken` (provider-neutral
   *  opaque token). Removed once all consumers migrate. */
  photoRef?: string | undefined;
  /** @deprecated Prefer photoRef. Legacy: stored URL (may fail). Resolvable format without key. */
  photoUrl?: string | undefined;
  /** Additional photo URLs for multi-image display (e.g. in summary view). */
  photoUrls?: string[] | undefined;
  /** ADR-0044: vendor-neutral catalog id ('<source>:<native>', e.g.
   *  'google:ChIJ...'). Coexists with placeId for one release; new
   *  writes set both, and the hydrate-time mapper backfills it for
   *  pre-migration records. Once all consumers prefer this field,
   *  placeId is removed. */
  catalogId?: string | undefined;
  /** ADR-0044: opaque token for the photo proxy
   *  (`/api/places/photo?token=...`). Provider-private content; treat
   *  as atomic. Coexists with photoRef for one release. */
  photoToken?: string | undefined;
  /** ToS §3.2.3 — author attribution for the chosen photo, captured at
   *  add-time so the timeline card can render the photographer credit
   *  without a fresh catalog round trip. */
  photoAttribution?: PhotoAttribution | undefined;
  address?: string | undefined;
  lat?: number | undefined;
  lng?: number | undefined;
  /** International phone, as returned by Google Places
   *  (`internationalPhoneNumber`, falling back to `nationalPhoneNumber`).
   *  Display string preserves the source formatting; the `tel:` link
   *  strips whitespace at dial time. Pro-tier Google SKU — populated
   *  when the Place Details request includes the `contact` feature. */
  phone?: string | undefined;
  /** Place's official website (`websiteUri`). Populated when the
   *  Place Details request includes the `contact` feature. */
  website?: string | undefined;
  /** Contact email. Not returned by Google Places — populated only
   *  via manual entry. */
  email?: string | undefined;
  types?: string[] | undefined;
  openingHours?: string[] | undefined;
  /** Start time in "HH:mm" format, e.g. "09:00". */
  startTime?: string | undefined;
  /** End time in "HH:mm" format. Duration is derived from startTime and endTime. */
  endTime?: string | undefined;
  /** Inline note content for round-trip storage on the wire. Mirrors
   *  Day/Transport/Custom — locally we read/write via notesMap[place.id],
   *  but on flush/hydrate we serialize through this field so notes survive
   *  cross-device sync. Should be left undefined in the client store
   *  (notesMap is the canonical local source). */
  notes?: string | undefined;
  /** Place-scoped reference materials (Wikipedia entry, walking map,
   *  trail map, official site, etc.). Same TripReference shape as the
   *  trip-level field; round-trips through the `days` sync field. */
  references?: TripReference[] | undefined;
  /** Per-item out-of-pocket costs for this place — activity tickets,
   *  audio guide, restaurant tab. Each item is independently
   *  attributable. Replaces the single-`Money` `cost` field of
   *  ADR-0048; legacy records are folded into a single-item array
   *  on hydrate. See `CostLineItem` and ADR-0069. */
  costs?: CostLineItem[] | undefined;
}

export type TransportMode =
  | 'walk'
  | 'car'
  | 'metro'
  | 'train'
  | 'bus'
  | 'bike'
  | 'taxi'
  | 'ferry'
  | 'flight'
  | 'other';

export interface Transport extends EntityAuthorshipFields {
  id: string;
  mode: TransportMode;
  startTime: string;
  /** End time. Duration is derived from startTime and endTime. */
  endTime?: string | undefined;
  /** Optional notes: airport, confirmation numbers, terminal, gate, etc. */
  notes?: string | undefined;
  /** True when auto-generated between consecutive places. Not stored in DB; computed at render. */
  isAutoGenerated?: boolean | undefined;
  /** For auto transports: the place we're traveling from. */
  fromPlaceId?: string | undefined;
  /** For auto transports: the place we're traveling to. */
  toPlaceId?: string | undefined;
  /** True when travel duration exceeds the available time gap. Computed, not stored. */
  hasTimeConflict?: boolean | undefined;
  /** Route distance in meters, when known. Auto-transports backfill this
   *  from the route provider response (Google / static estimator); manual
   *  transports leave it unset. Display layer converts to km/mi per user pref. */
  distanceMeters?: number | undefined;
}

export interface CustomEntry extends TimeRange, EntityAuthorshipFields {
  id: string;
  title: string;
  subtitle?: string | undefined;
  notes?: string | undefined;
  /** Required for CustomEntry — narrows the optional TimeRange.startTime. */
  startTime: string;
  /** End time. Duration is derived from startTime and endTime. */
  endTime?: string | undefined;
  /** Per-item out-of-pocket costs — meals, fees, misc. spend the
   *  user wants to track. Same shape as `Place.costs`; rolled up
   *  identically. See `CostLineItem` and ADR-0069. */
  costs?: CostLineItem[] | undefined;
}

/**
 * A single anchor of a Journey — origin or destination. Carries the full
 * ISO datetime (not HH:mm) because Journeys can cross day boundaries.
 */
export interface JourneyAnchor {
  /** Required picked-place snapshot (ADR-0054 / ADR-0055). Catalog id
   *  for picker writes; `'legacy:<journeyId>:<origin|destination>'`
   *  for entries migrated from pre-ADR-0055 flat fields. */
  place: PlaceRef;
  /** Optional user-supplied display label (e.g. "Mom's house flight"
   *  instead of "JFK Airport"). When set, cards / iCal SUMMARY prefer
   *  this over `place.name`. Coords and address still come from
   *  `place`. */
  customLabel?: string | undefined;
  /** IATA / station code. */
  code?: string | undefined;
  /** Full ISO datetime, e.g. '2026-10-21T23:00'. */
  time: string;
  terminal?: string | undefined;
  gate?: string | undefined;
  platform?: string | undefined;
  /** IANA timezone name for this anchor's local time, e.g.
   *  'America/New_York' or 'Europe/Paris'. When set, `time` is
   *  interpreted as a wall-clock time in this zone, which is what
   *  enables correct duration math across timezones (a JFK 23:00 → CDG
   *  11:00 flight is 6 h, not 12 h). Auto-populated from the picked
   *  place's lat-lng. Stays on the anchor (not on `place`) per
   *  ADR-0047 — `timezone` describes the anchor's role in the journey,
   *  not the underlying place. */
  timezone?: string | undefined;
}

/**
 * Scheduled long-haul transit (flight, intercity train, long-distance
 * bus, ferry, transfer, car-rental segment). Distinct from `Transport`,
 * which is an auto-generated short-hop connector between adjacent Places.
 *
 * A Journey's anchors are transit infrastructure (airports, stations) —
 * they aren't itinerary stops in their own right. ISO datetimes on origin
 * and destination let a Journey naturally span across days (overnight
 * flights / sleeper trains). See ADR-0043.
 */
export interface Journey extends EntityAuthorshipFields {
  id: string;
  mode: 'flight' | 'train' | 'bus' | 'ferry' | 'car-rental' | 'transfer';
  origin: JourneyAnchor;
  destination: JourneyAnchor;
  /** 'American Airlines', 'SNCF', etc. */
  carrier?: string | undefined;
  /** 'AA123', 'TGV 6543', etc. */
  serviceNumber?: string | undefined;
  seat?: string | undefined;
  /** 'Economy', 'Business', 'First', or operator-specific class. */
  travelClass?: string | undefined;
  booking?: Booking | undefined;
  /** Inline note content for round-trip storage on the wire. Mirrors
   *  Place/Transport — locally we read/write via notesMap[journey.id]. */
  notes?: string | undefined;
}

export interface Day {
  id: string;
  date: string;
  /** User-provided label override. When absent, display code computes "Day N / Town". */
  label?: string | undefined;
  places: Place[];
  transports?: Transport[] | undefined;
  customs?: CustomEntry[] | undefined;
  /** @deprecated ADR-0043 v2 — Journeys are now Trip-scoped (Trip.journeys[]).
   *  Field kept for one release so persisted local data + legacy server
   *  payloads can hydrate; sync-manager migrates these up to Trip.journeys
   *  on first read. New writes always go to Trip.journeys. Will be removed
   *  in a follow-up. */
  journeys?: Journey[] | undefined;
  /** Optional visiting hours for the day, e.g. "9:00 – 18:00" */
  hours?: string | undefined;
  /** Optional notes for the day. */
  notes?: string | undefined;
  /** User overrides for auto-transport mode (walk/metro/car). Key: "fromPlaceId-toPlaceId". */
  autoTransportModeOverrides?: Record<string, TransportMode> | undefined;
}

/**
 * Multi-day accommodation — hotel, Airbnb, hostel, host stay. Lives at
 * trip level rather than day level because a single Stay typically spans
 * multiple Days (a 4-night hotel = Days 2–5). See ADR-0043.
 */
export interface Stay extends EntityAuthorshipFields {
  id: string;
  /** Required picked-place snapshot (ADR-0054 / ADR-0055). Catalog id
   *  for picker writes; `'legacy:<stayId>'` for entries migrated from
   *  pre-ADR-0055 flat fields. */
  place: PlaceRef;
  /** Optional user-supplied display label (e.g. "Beach House"
   *  instead of "Hotel Fasano"). When set, cards / iCal SUMMARY
   *  prefer this over `place.name`. Coords and address still come
   *  from `place`. */
  customLabel?: string | undefined;
  /** Hero photo URL (cached). */
  heroUrl?: string | undefined;
  /** ISO datetime of check-in, e.g. '2026-10-21T15:00'. */
  checkIn: string;
  /** ISO datetime of check-out. */
  checkOut: string;
  /** IANA timezone name for the property, e.g. 'America/New_York'.
   *  Mirrors the JourneyAnchor.timezone convention (ADR-0047).
   *
   *  CURRENT STATE (ADR-0081 slice 3): the booking-parser adapter
   *  WRITES this field from `HotelExtract.timezoneIana` so the signal
   *  the model produced survives the entity round-trip. NO UI / sync
   *  / duration-math consumer reads it yet — DayStayStrip + iCal
   *  export still use naive wall-clock interpretation. The field
   *  exists today to persist forward-compatible data; a follow-up
   *  PR will wire the readers. Until then, treat it as advisory. */
  timezone?: string | undefined;
  /** 'King Suite', 'Whole apartment', etc. */
  roomType?: string | undefined;
  /** 'Room 412', 'Apt 3B'. */
  unitNumber?: string | undefined;
  booking?: Booking | undefined;
  /** Inline note content for round-trip storage on the wire.
   *  Locally we read/write via notesMap[stay.id]. */
  notes?: string | undefined;
}

export type CardTag = 'important' | 'reach-goal' | 'missing-info';

/**
 * A person travelling on a trip. Referenced by
 * `CostLineItem.paidBy` and `splitWith` (ADR-0069 / ADR-0059).
 * See ADR-0057. Participants are trip-scoped — created manually,
 * then optionally linked to a Kiips user account via `userId`
 * (matching `TripShare.userId`) so cost line items can flow into
 * a per-user report later.
 *
 * `displayName` is the canonical name. Identity is shown via
 * `avatarUrl` when set, falling back to initials on a hashed
 * gradient — see ADR-0057 ("Identity rendering" amendment).
 *
 * Note: earlier revisions of this type carried `friendlyName`
 * (optional nickname) and `colorIndex` (deprecated palette
 * index). Both were removed in the ADR-0057 inline-edit
 * amendment; legacy fields on stored / persisted trips are
 * stripped on hydrate by `migrateTripParticipants`.
 */
export interface TripParticipant {
  /** Client-minted via `generateId()`. Stable across renames /
   *  link operations so cost entries can reference it durably. */
  id: string;
  /** Canonical name. 1..120 chars. */
  displayName: string;
  /** Denormalised avatar URL for the linked user, captured at
   *  link / quick-add time (parallels how `TripShare.displayName`
   *  denormalises the user's name). Absent for unlinked
   *  participants; the UI falls back to an initials gradient. */
  avatarUrl?: string | undefined;
  /** Set when the participant has been linked to a Kiips user
   *  account — same shape as `TripShare.userId`. Linking never
   *  overwrites `displayName`; unlinking clears this and
   *  `avatarUrl`. */
  userId?: string | undefined;
}

/**
 * A free-form spending bucket on a trip — phase 2 of the cost-
 * tracking feature (ADR-0058). Each `CostLineItem` references
 * one via `categoryId`. Per-trip and user-editable (rename /
 * recolour / remove); a starter set is seeded by the client on
 * trip create. See `DEFAULT_TRIP_CATEGORY_NAMES`.
 *
 * Shape mirrors `TripParticipant` deliberately: a stable id, a
 * required name, and an optional `colorIndex` into the
 * participant palette so cards and rollups can colour-code
 * categories the same way they colour-code people.
 */
export interface TripCategory {
  /** Client-minted via `generateId()`. Stable across renames so
   *  cost line items can reference it durably. */
  id: string;
  /** Free-form display label. 1..120 chars. */
  name: string;
  /** 0..7, indexes into the same `polaroidRainbow` palette used
   *  for participants. When absent, the UI assigns one
   *  deterministically via `hash(id) % palette.length`. */
  colorIndex?: number | undefined;
}

/**
 * Names used to seed `Trip.categories` on trip create. Common
 * travel-spend buckets, ordered to match the palette so the first
 * six categories naturally span the rainbow before wrapping. The
 * names are read-only intent — the user can immediately
 * rename / delete / recolour any of them after the trip lands.
 */
export const DEFAULT_TRIP_CATEGORY_NAMES = [
  'Food',
  'Transport',
  'Lodging',
  'Activities',
  'Shopping',
  'Other',
] as const;

export interface Trip extends DateRange {
  id: string;
  name: string;
  /** Denormalized display label for the trip's destination. Set by
   *  the new-trip picker to `destinationPlace.name`; legacy trips
   *  carry their original free-text. Read-only at the UI layer now
   *  that the picker is the only writer. */
  destination: string;
  /** Picked destination as a single place snapshot — see ADR-0054.
   *  Carries name, optional address, coords, and country code. The
   *  references pipeline reads `countryCode` directly when present
   *  (skips the free-text destination parse). Absent for legacy
   *  trips and for trips imported without coords. */
  destinationPlace?: PlaceRef | undefined;
  /** Re-declared from DateRange for documentation; same shape. 'YYYY-MM-DD'. */
  startDate: string;
  endDate: string;
  preferences?: TripPreferences | undefined;
  days: Day[];
  /** Multi-day accommodation across the trip. See `Stay` and ADR-0043.
   *  Optional for backward compatibility with legacy trip records. */
  stays?: Stay[] | undefined;
  /** Scheduled long-haul transit across the trip. Trip-scoped (not
   *  day-scoped) because a single Journey can span multiple days
   *  (overnight flights, sleeper trains). Per-day rendering is derived
   *  from origin/destination datetime. See ADR-0043 v2. */
  journeys?: Journey[] | undefined;
  /** Per-trip wishlist — candidate places saved while exploring (the
   *  Discover tab), not yet assigned to a day. Stored as `Place`
   *  objects with `startTime`/`endTime` left undefined; they only gain
   *  times when promoted into a day via `moveWishlistItemToDay`.
   *  Trip-scoped and synced on its own `wishlist` wire field, like
   *  stays/journeys. Optional so existing trips hydrate cleanly. */
  wishlist?: Place[] | undefined;
  /** People travelling on this trip. Referenced by
   *  `CostLineItem.paidBy` / `splitWith` (ADR-0069). Optional so
   *  existing trips hydrate cleanly with no participants.
   *  See ADR-0057. */
  participants?: TripParticipant[] | undefined;
  /** Spending buckets for this trip. Referenced by
   *  `CostLineItem.categoryId` (ADR-0069). Optional so existing
   *  trips hydrate cleanly without categories; new trips get the
   *  defaults seeded by the client. See ADR-0058. */
  categories?: TripCategory[] | undefined;
  /** JSON-stringified notes map for DynamoDB serialization. */
  notes?: string | undefined;
  /** Entity ID -> tag mapping for card priority/status indicators. */
  tags?: Record<string, CardTag> | undefined;
  /** Place id whose photo is "locked" as the trip cover. When unset,
   *  the UI picks a stable photo from the trip's places. Set via the
   *  swipe-to-pick gallery on the trip detail hero. */
  heroPlaceId?: string | undefined;
  /** Reference materials attached to the trip — auto-suggested maps,
   *  visa info, country cards, transit maps, etc. Trip-level (not
   *  day-scoped). See TripReference for the per-entry shape. */
  references?: TripReference[] | undefined;
  /** User-uploaded files attached anywhere on this trip — booking
   *  PDFs, receipts, vault documents (passport, insurance). Trip-
   *  scoped (not nested per-entity) so "all attachments on this trip"
   *  is a single filter; per-entity views filter on
   *  `attachedTo.entityId`. Bytes live in Cloudflare R2 — see
   *  ADR-0060. */
  attachments?: Attachment[] | undefined;
  /** ISO timestamp of the last server-side write. Drives multi-device live
   *  reload — clients skip re-downloading payload bodies when this matches
   *  the locally cached value. */
  updatedAt?: string | undefined;
  /** Marker for the local sample itinerary seeded for anonymous visitors
   *  (`DEMO_TRIP_ID`). The sync layer never enqueues a demo trip for
   *  upload, even if it ends up in `dirtyTrips` from a legacy state.
   *  Set on the seed and treated as an invariant — production trips
   *  never carry this. */
  isDemo?: true | undefined;
}

/** Role for trip sharing. Used when backend persistence exists. */
export type ShareRole = 'admin' | 'editor' | 'viewer';

/** Share record: links a trip to a user with a role (for backend). */
export interface TripShare {
  tripId: string;
  userId: string;
  email?: string | undefined;
  /**
   * The shared-with user's displayName, populated by the listShares
   * route via a per-share user lookup so the client can render a real
   * name in the share list and quick-add chips. Stays optional because
   * not every persisted share carries it — readers should fall back to
   * email's local-part, then a userId fragment.
   */
  displayName?: string | null | undefined;
  /**
   * The shared-with user's avatarUrl, populated by the same listShares
   * enrichment that fills `displayName`. The PeopleTab quick-add flow
   * captures it onto `TripParticipant.avatarUrl` so the participant
   * avatar follows the linked user. Null when the user has no avatar
   * configured; absent when enrichment hasn't been run.
   */
  avatarUrl?: string | null | undefined;
  role: ShareRole;
  createdAt: string;
  /**
   * Owner of the trip this share grants access to. Denormalised here
   * so post-ADR-0051 share-side reads can address the composite-key
   * Trips table without a GSI hop. Optional during the migration
   * window (legacy records have no value); back-fill populates and
   * the cutover treats it as required.
   */
  ownerId?: string | undefined;
}

/** Invite link: a token that grants access to a trip. */
export interface InviteLink {
  linkId: string;
  tripId: string;
  role: ShareRole;
  createdBy: string;
  email?: string | undefined;
  isPending: boolean;
  createdAt: string;
  /** Denormalised trip owner — see TripShare.ownerId. */
  ownerId?: string | undefined;
}

/** @deprecated ADR-0044 — prefer `PlaceCatalogPrediction` from
 *  `@my-fullstack-app/trpc-types`. Removed once all consumers migrate. */
export interface PlaceAutocompletePrediction {
  placeId: string;
  description: string;
  mainText?: string | undefined;
  secondaryText?: string | undefined;
}

/** @deprecated ADR-0044 — prefer `PlaceCatalogEntry` from
 *  `@my-fullstack-app/trpc-types`. Removed once all consumers migrate. */
export interface PlaceDetails {
  placeId: string;
  name: string;
  /** Google Places photo reference. Use getPlacePhotoUrl to fetch working photoUri. */
  photoRef?: string | undefined;
  /** Resolvable photo URL (no API key). Kept for backwards compatibility. */
  photoUrl?: string | undefined;
  address?: string | undefined;
  lat?: number | undefined;
  lng?: number | undefined;
  /** Pro-tier — set when `contact` feature was requested. */
  phone?: string | undefined;
  /** Pro-tier — set when `contact` feature was requested. */
  website?: string | undefined;
  types?: string[] | undefined;
  openingHours?: string[] | undefined;
  /** ToS §3.2.3 — author attribution for the chosen photo, captured at
   *  lookup time. Mirrors the array index of `photoRef` (the first
   *  photo on the place). */
  photoAttribution?: PhotoAttribution | undefined;
}

/**
 * Categorisation of an attached reference. Used to pick the right icon
 * + small descriptor in the UI; not validated server-side.
 */
export type ReferenceType =
  | 'country-info'
  | 'visa'
  | 'transit-map'
  | 'walking-map'
  | 'travel-advisory'
  | 'wikivoyage'
  | 'embassy'
  | 'holidays'
  | 'currency'
  | 'emergency-contact'
  | 'link'
  | 'other';

/**
 * Where a reference originated. Drives filtering ("hide auto-suggested",
 * "show only mine"); never affects storage. */
export type ReferenceSource = 'auto' | 'manual' | 'ai';

/**
 * A single reference attached to a trip — a map, document, info card,
 * external link, etc. Cross between a Document and a structured fact
 * sheet: simple URLs use `url`; structured data (currency, plug type,
 * emergency numbers) goes on `payload`.
 *
 * References are tied to the trip, not to a specific day or place.
 * Auto-suggested references appear with `source: 'auto'` and can be
 * dismissed without removing them — the suggester reads `dismissed`
 * to avoid re-adding the same hint twice.
 */
export interface TripReference {
  id: string;
  type: ReferenceType;
  /** Headline shown on the card. */
  title: string;
  /** One-liner shown under the title. */
  description?: string | undefined;
  /** Optional URL to open / fetch. */
  url?: string | undefined;
  /** Optional phone number. Rendered as a tappable `tel:` affordance on
   *  the card. Top-level (not on `payload`) so the UI can render the
   *  affordance without type narrowing — parallel to `url`. Display
   *  string preserves the source formatting (e.g. "+81 3 5555 1234");
   *  the `tel:` link strips whitespace at dial time. */
  phone?: string | undefined;
  /** Optional structured payload (e.g. REST Countries returns currency,
   *  language, plug, calling code inline). UI inspects `type` to decide
   *  how to render. Kept loose so each source can ship its own shape. */
  payload?: Record<string, unknown> | undefined;
  /** Optional thumbnail (PDF first page, favicon, flag). */
  thumbnail?: string | undefined;
  /** Where the reference came from. */
  source: ReferenceSource;
  /** When auto-suggested entries aren't useful, the user can dismiss
   *  them. Dismissed entries are kept on the trip so re-running the
   *  suggester doesn't re-add the same hint. UI hides them. */
  dismissed?: boolean | undefined;
  /** Pinned references float to the top of the references list. */
  pinned?: boolean | undefined;
  /** ISO timestamp when added. */
  addedAt: string;
}

/**
 * De-dup key for a TripReference. Used by both the client store
 * (when appending new entries) and the server suggester (when filtering
 * candidates against what's already on the trip) so the two layers
 * agree on what counts as the same reference.
 *
 * Key composition: `${type}::${title.toLowerCase()}::${phone||''}` —
 * with whitespace stripped from the phone so "+1 555 1234" and
 * "+15551234" collapse. Phone is part of the key so two emergency
 * contacts both titled "Hotel" with different numbers (e.g. ryokan vs.
 * business hotel) stay distinct; auto-suggested re-runs collapse
 * because their phone is stable.
 */
export function referenceDedupKey(r: { type: string; title: string; phone?: string | undefined }): string {
  const phone = (r.phone ?? '').replace(/\s+/g, '');
  return `${r.type}::${r.title.toLowerCase()}::${phone}`;
}

// ─── Attachments (ADR-0060) ──────────────────────────────────────

/**
 * Categorisation of an attached file. Drives the icon + small
 * descriptor on the attachment card; not validated server-side.
 * Distinct from MIME type — a PDF can be a confirmation, ticket, or
 * receipt and the user picks which.
 */
export type AttachmentLabel =
  | 'confirmation'
  | 'ticket'
  | 'boarding-pass'
  | 'receipt'
  | 'invoice'
  | 'visa'
  | 'passport'
  | 'insurance'
  | 'vaccination'
  | 'other';

/**
 * Anchor describing what an Attachment is attached to. When omitted,
 * the attachment is trip-scoped (Document Vault entry — no parent
 * entity). The discriminator lives on the Attachment rather than the
 * parent so a single trip-level walk (`Trip.attachments`) is the
 * source of truth: "show every attachment on this trip" is one
 * filter, not a tree traversal.
 *
 * `entityId` refers to:
 *   - `Journey.id` when kind === 'journey'
 *   - `Stay.id` when kind === 'stay'
 *   - `Place.id` when kind === 'place'
 *   - `CustomEntry.id` when kind === 'custom'
 *   - `Journey.id` or `Stay.id` when kind === 'booking' — the
 *     'booking' kind is reserved for reservation paperwork
 *     (confirmation PDF, e-ticket); other 'journey'/'stay' kinds
 *     cover boarding-pass screenshots, room photos, etc.
 */
export interface AttachmentAnchor {
  kind: 'booking' | 'place' | 'custom' | 'journey' | 'stay';
  entityId: string;
}

/**
 * A user-uploaded binary file attached to a trip. Bytes live in
 * Cloudflare R2 at `storageKey`; this record carries only metadata.
 * See ADR-0060 for the full design — bucket layout, presign/confirm
 * flow, quota enforcement, sweep semantics.
 *
 * Lifecycle:
 *   1. Client calls `attachments.createUploadUrl` → server reserves
 *      bytes against `Trip.attachmentBytes`, mints a presigned PUT
 *      URL, writes this record with `state: 'pending'`.
 *   2. Client uploads bytes directly to R2 (PUT).
 *   3. Client calls `attachments.confirm` → server HEADs R2, verifies
 *      `Content-Length === size`, flips state to `'uploaded'`.
 *   4. Pending entries with no confirm within 24 h are swept by a
 *      daily cron (HEAD R2, then either confirm or hard-delete row +
 *      orphan key). UI hides `state === 'pending'` from non-uploader
 *      viewers.
 */
export interface Attachment {
  /** Server-minted UUID returned by `attachments.createUploadUrl` —
   *  the client adopts it for optimistic insert and uses it on every
   *  subsequent mutation. Stable across re-anchors and label edits
   *  so undo retains identity. */
  id: string;
  /** R2 object key. Format:
   *  `trips/{tripId}/attachments/{id}/{sanitisedFilename}`. The
   *  filename in the key is sanitised to `[A-Za-z0-9._-]`; the
   *  original filename is preserved on `filename` and surfaces via
   *  `Content-Disposition` on the presigned GET. The client never
   *  builds this key by hand — server constructs it at presign time. */
  storageKey: string;
  /** Original filename from the picker. Free-form; preserved verbatim
   *  for display and `Content-Disposition`. */
  filename: string;
  /** MIME type as reported by the picker. Server validates against an
   *  allow-list (PDF, JPEG/PNG/HEIC/HEIF/WebP, .eml) at presign time
   *  and signs the upload with this type so a client can't switch
   *  post-presign. */
  mimeType: string;
  /** File size in bytes. Server treats this as a budget reservation
   *  at presign and re-reads R2 `Content-Length` on confirm. */
  size: number;
  /** Optional categorisation. Drives the icon + small descriptor in
   *  the attachment card; not validated server-side. */
  label?: AttachmentLabel | undefined;
  /** What this attachment is anchored to. Absent for trip-level
   *  Document Vault entries. */
  attachedTo?: AttachmentAnchor | undefined;
  /** User who uploaded this attachment — same shape as
   *  `TripShare.userId`. Required for shared trips so collaborators
   *  can see provenance; set server-side from the auth context (never
   *  trusted from the client). */
  uploadedBy: string;
  /** ISO timestamp set at presign. */
  uploadedAt: string;
  /** Upload state. 'pending' until the client confirms; 'uploaded'
   *  after the server HEAD-verifies R2. */
  state: 'pending' | 'uploaded';
  /** Optional R2 key for a server-generated thumbnail (PDF page 1
   *  rasterised to JPEG, large images downsized to 512px long edge).
   *  Lazy: absent until the first thumbnail request triggers
   *  generation. UI falls back to a MIME-type icon when absent. */
  thumbnailKey?: string | undefined;
  /** Structured fields extracted from the attachment via AI vision —
   *  ADR-0083. Present only for attachments labelled `passport`,
   *  `visa`, `insurance`, or `vaccination` (and future schema-driven
   *  labels). `extracted.label` must match `Attachment.label`. The
   *  field set is keyed off `DocumentSchema.fields[].name` — keep
   *  field access schema-aware (see `getDocumentSchema`) rather than
   *  destructuring known names at compile time. Owner-only: the API
   *  redacts this field for non-owner viewers per ADR-0083 §Q6b. */
  extracted?: ExtractedDocument | undefined;
}

// ─── Document extraction (ADR-0083) ──────────────────────────────

/** A persisted, schema-validated extraction of structured fields from
 *  an attached document. The shape is data-driven: the field set
 *  depends on `label`, looked up from the compile-time
 *  `DocumentSchema` registry. The persisted `schemaVersion` is the
 *  registry version in force at extraction time — the snapshot
 *  renderer tolerates stale versions by skipping fields the current
 *  schema no longer declares; it surfaces a "schema updated —
 *  re-extract for the latest field set" hint when an `important`
 *  field is now missing. See ADR-0083 §Q13. */
export interface ExtractedDocument {
  /** Must equal `Attachment.label` and match a registry entry in
   *  `DOCUMENT_SCHEMAS`. */
  label: AttachmentLabel;
  /** Snapshot of `DocumentSchema.schemaVersion` at extraction time.
   *  Records keyed against an older version are read tolerantly: the
   *  snapshot renderer normalises field-aliased keys and skips
   *  fields the current schema no longer declares. */
  schemaVersion: number;
  /** Field bag keyed by `DocumentSchema.fields[].name`. Values are
   *  typed by `DocumentField.type` (server validates at confirm time
   *  via `validateExtractedFields`). Every field is optional on the
   *  wire — a bad scan may miss values; UI surfaces missing fields
   *  as "couldn't read — fill in?" rather than blocking save. */
  fields: Record<string, ExtractedFieldValue>;
  /** Per-field self-rated confidence — RESERVED for ADR-0083 §Q14.
   *  v1 leaves this undefined; a future change adds wrapper field
   *  types that populate it without a wire-contract break. */
  confidenceByField?: Record<string, 'high' | 'medium' | 'low'> | undefined;
  /** Server-set on confirm. */
  extractedAt: string;
  /** Which model produced the initial extraction. */
  extractedModel: 'sonnet-4-6' | 'haiku-4-5';
}

/** Permitted value shapes inside `ExtractedDocument.fields`. Numbers
 *  cover counts (`max_stay_days`); strings cover free-form text,
 *  dates (ISO 8601), enums, country codes, phone, email; `Money`
 *  covers currency amounts; string arrays cover multi-string fields
 *  and the multi-country list; the `'worldwide'` literal is the
 *  sentinel for the multi-country "worldwide" toggle on insurance
 *  coverage. See ADR-0083 §Q2. */
export type ExtractedFieldValue =
  | string
  | number
  | Money
  | readonly string[]
  | 'worldwide';

/** UI / runtime type discriminator for `DocumentField`. Each value
 *  determines how the field is rendered in the review form, what
 *  Anthropic JSON Schema fragment is emitted in the tool definition,
 *  and what the Zod validator at confirm time expects. Adding a new
 *  type is non-breaking; older records that never used the new type
 *  read unchanged. See ADR-0083 §Q5. */
export type DocumentFieldType =
  | 'string'
  | 'date'
  | 'datetime'
  | 'number'
  | 'enum'
  | 'country-code'
  | 'money'
  | 'multi-string'
  | 'multi-country'
  | 'phone'
  | 'email';

/** One field on a `DocumentSchema`. `name` is the wire-contract
 *  identifier — rename = `DocumentSchema.schemaVersion` bump (plus
 *  optional alias in `fieldAliases`). Field-level `important: true`
 *  raises the field above the fold in the UI and emphasises it in
 *  the assistant system prompt's field-coverage list. */
export interface DocumentField {
  name: string;
  label: string;
  type: DocumentFieldType;
  /** Required when `type === 'enum'`. The Anthropic tool schema
   *  pins these as the allowed enum values; the Zod validator
   *  rejects anything else. */
  options?: readonly string[] | undefined;
  /** UI placeholder + few-shot anchoring hint. */
  hint?: string | undefined;
  /** Highlighted first in the form; flagged as important in the
   *  assistant system prompt's per-label field-coverage list. */
  important?: boolean | undefined;
}

/** Future-proofing reservation for cross-field validation rules —
 *  ADR-0083 §Q16. v1 declares this as `never` (no constraints
 *  supported); future ADRs can extend the union without breaking
 *  existing schemas (which all declare an empty constraints list or
 *  none). */
export type DocumentConstraint = never;

/** Future-proofing reservation for specialised per-label
 *  preprocessors — ADR-0083 §Q22. The schema's `preprocessors` slot
 *  is currently typed as `never[]` (no preprocessors supported in
 *  v1); a future ADR introducing e.g. MRZ checksum parsing will
 *  extend the union and the schema runtime to invoke each
 *  preprocessor before the vision call. */
export type PreprocessorRef = never;

/** One label's structured-extraction definition. The registry maps
 *  `AttachmentLabel → DocumentSchema | undefined`; labels without a
 *  schema (`confirmation`, `ticket`, `boarding-pass`, etc. — those
 *  belong to ADR-0081) return `undefined`. */
export interface DocumentSchema {
  /** Matches `AttachmentLabel`. */
  label: AttachmentLabel;
  /** UI display name ('Passport', 'Visa', …). English at v1;
   *  localisation can wrap this without changing the registry shape.
   *  See ADR-0083 §Q17. */
  displayName: string;
  /** Field whose value bounds the document's validity window. Used
   *  by the assistant snapshot resolver to pick the `validForTrip`
   *  variant when multiple attachments of the same label exist
   *  (ADR-0083 §Q11). Omit when the document has no validity end
   *  (lifelong vaccinations, ID cards without an expiry). */
  validityEndField?: string | undefined;
  /** When true, a missing `validityEndField` value on an extracted
   *  record is treated as "covers any date" — used for documents
   *  where blank-means-lifelong (vaccination records for MMR /
   *  polio, etc.). When false (the default), a missing value means
   *  "we don't know the expiry" and the record does NOT qualify as
   *  `validForTrip` — critical for passport / visa / insurance
   *  where missing expiry indicates an extraction failure, not
   *  lifelong validity. ADR-0083 §Q11. */
  validityEndOptional?: boolean | undefined;
  /** Ordered field list. UI renders top-to-bottom in this order;
   *  `important: true` fields are pinned first within each section. */
  fields: readonly DocumentField[];
  /** Worked example for the Claude few-shot. Map of `field.name →
   *  value`. Keys must be a subset of `fields[].name` (registry test
   *  enforces). Used by the prompt builder to anchor the model on
   *  what a good extraction looks like for this label. */
  fewShotExample: Record<string, ExtractedFieldValue>;
  /** Old-name → new-name alias map for field renames across schema
   *  versions. v1 schemas have none; populated when bumping a
   *  schemaVersion that renames a field, so older
   *  `ExtractedDocument` records remain readable. */
  fieldAliases?: Readonly<Record<string, string>> | undefined;
  /** Reserved per Q16; v1 leaves this empty. */
  constraints?: readonly DocumentConstraint[] | undefined;
  /** Reserved per Q22; v1 leaves this empty. */
  preprocessors?: readonly PreprocessorRef[] | undefined;
  /** Bump on breaking schema changes (field rename / drop / retype).
   *  Field additions are non-breaking. */
  schemaVersion: number;
}

/**
 * Per-purpose tracking-consent decision. Captured by the consent
 * sheet on first launch and re-prompted when `version` is bumped.
 * Stored on `UserPreferences` so it syncs across the user's
 * devices; anonymous users keep a mirror in AsyncStorage under
 * `consent:v1`. See ADR-0065.
 */
export interface ConsentDecision {
  /** PostHog product analytics + error reporting — ADR-0063 / ADR-0065.
   *  Covers both usage events and crash/exception capture, which share a
   *  single PostHog connection (the standalone `errorReports` purpose was
   *  folded in at consent version 2). */
  analytics: boolean;
  /** TravelPayouts affiliate CTAs + partner-side tracking cookie — ADR-0064. */
  affiliates: boolean;
  /** ISO timestamp of the decision. */
  decisionAt: string;
  /** Bumped when the privacy policy materially changes; an
   *  out-of-date version triggers a re-prompt. */
  version: number;
}

/**
 * User-level preferences synced across devices via the `userPrefs` tRPC
 * router. Distinct from per-trip data — these follow the user, not the
 * trip. Stored as a single record per user.
 *
 * Each field is optional so partial updates from older clients (or
 * older schema versions) don't clobber newer fields the server might
 * carry. Last-write-wins on each top-level key.
 */
/**
 * Sparse layer-preferences record shared by `layerPrefs` and
 * `reviewLayerPrefs` on `UserPreferences`. `explicit` enumerates the
 * layer ids the user has directly toggled; `values` carries those
 * ids' chosen values. Layers absent from `explicit` derive their
 * value from the client's current registry default — letting a
 * registry-default change reach existing users without re-pinning
 * their stale snapshot. See ADR-0082.
 */
export interface LayerPrefRecord {
  /** Layer ids the user has explicitly toggled. */
  explicit: string[];
  /** Per-explicit-id values. Keys outside `explicit` MUST be
   *  ignored on read — they exist (if at all) as harmless leftovers
   *  from a buggy writer and should not influence reads. */
  values: Record<string, boolean>;
}

export interface UserPreferences {
  /** Trip ids the user has marked as favourite. */
  favoriteTripIds?: string[] | undefined;
  /** Plan-timeline overlay toggles. `explicit` is the set of layer
   *  ids the user has directly opted into a value for; `values`
   *  carries the chosen value for each explicit id. Layers absent
   *  from `explicit` fall through to the client's current registry
   *  default at read time — so a registry-default change after the
   *  user signed up reaches them automatically (ADR-0082). */
  layerPrefs?: LayerPrefRecord | undefined;
  /** Review-tab section toggles. Same shape as `layerPrefs`. */
  reviewLayerPrefs?: LayerPrefRecord | undefined;
  /** Theme mode override. */
  themeMode?: 'light' | 'dark' | 'system' | undefined;
  /** ISO 3166-1 alpha-2 country code of the user's citizenship —
   *  e.g. 'US', 'JP'. Powers the targeted visa lookup
   *  ("Visa requirements for {nationality} citizens"). */
  nationality?: string | undefined;
  /** ISO 4217 currency code — e.g. 'USD'. Drives cost display and
   *  future FX rollups. */
  preferredCurrency?: string | undefined;
  /** User's home base as a single picked-place snapshot — see
   *  ADR-0054. Carries name, optional address, 2-decimal-rounded
   *  coords, and derived country code. Replaces the five-field
   *  `home{City,Country,Lat,Lng,CanonicalName}` sprawl. Absent until
   *  the user picks one; replaced (never unset) via the profile picker. */
  home?: PlaceRef | undefined;
  /** Temperature unit for weather displays. */
  temperatureUnit?: 'celsius' | 'fahrenheit' | undefined;
  /** Distance unit for transport + map scale displays. */
  distanceUnit?: 'km' | 'mi' | undefined;
  /** Clock format for place times. */
  timeFormat?: '12h' | '24h' | undefined;
  /** When false, in-app haptics (tap confirmations, swipe commits,
   *  destructive warnings) are muted regardless of the system Reduce
   *  Motion flag. The mount-side OR-s this with the system flag so
   *  either source can suppress. Defaults to true when absent (ADR-0079). */
  hapticsEnabled?: boolean | undefined;
  /** Tracking-consent decision — see ADR-0065. */
  consent?: ConsentDecision | undefined;
  /** ADR-0084 — hide every "Added by AI" badge across the whole app.
   *  Defaults false; once toggled on, no new attribution badges
   *  appear (the underlying `createdBy: 'assistant'` field stays on
   *  the entity for audit purposes). */
  hideAiBadges?: boolean | undefined;
  /** ADR-0084 — set of entity ids whose AI badge the user has tapped
   *  to dismiss. Smaller mechanism than `hideAiBadges`: dismisses one
   *  card at a time without disabling future surfaces. The list is
   *  bounded by the trip lifetime; cleared on trip delete via the
   *  existing prefs migration. */
  acknowledgedAiAdds?: string[] | undefined;
  /** ADR-0084 — when true, the assistant's Tier-C destructive writes
   *  (delete place, remove transport) skip the "type 'remove' to
   *  confirm" gate and behave like Tier-B (one-tap Accept). Off by
   *  default; the trade-off is well-marked in the chat settings
   *  surface so the user knows what they're opting into. */
  allowAiDeletes?: boolean | undefined;
  /** ADR-0091 — when true, the new-trip spotlight walkthrough never
   *  auto-starts. Set by the walkthrough's "Don't show again" checkbox
   *  or the /profile toggle. Defaults false. Synced across devices so a
   *  user who dismissed it on one device isn't re-nagged on another. */
  tutorialDisabled?: boolean | undefined;
  /** ADR-0091 — ISO timestamp the user last finished/completed the
   *  walkthrough. Lets us avoid re-prompting users who have already
   *  seen the full tour, and seeds a future version-bumped re-tour. */
  tutorialSeenAt?: string | undefined;
  /** ISO timestamp of the last server-side write. Used to skip
   *  no-op hydrations when the local cache is already current. */
  updatedAt?: string | undefined;
}
